import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import _get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { connect, useDispatch } from 'react-redux';

import DangerousHTML from 'Components/Utility/DangerousHTML';
import Meta from 'Components/Utility/SEO/Meta';
import ItemList from 'Components/Product/Seo/ItemList';
import productToHit from 'Helpers/Product/ProductToHit';
import BundleBreadcrumbs from 'Components/Utility/SEO/BundleBreadcrumbs';
import VoucherDelivery from 'Components/Product/ProductOffers/VoucherDelivery';
import FreeProductContainer from 'Containers/Product/FreeProductContainer';
import BundleProductBar from 'Components/Bundle/BundleProductBar';
import EbookTrustBar from 'Components/Ebook/EbookTrustBar';
import PickAndMixNoProducts from 'Components/PickAndMix/PickAndMixNoProducts';
import PickAndMixDesktopLayout from 'Components/PickAndMix/PickAndMixDesktopLayout';
import PickAndMixMobileLayout from 'Components/PickAndMix/PickAndMixMobileLayout';
import useViewport from 'Hooks/useViewport';
import BundleCarousel from 'Components/Bundle/BundleCarousels/BundleCarousel';
import { sortPickAndMixProducts, messages, getBundleTypeMessage } from 'Components/PickAndMix/PickAndMixHelper';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import { history } from 'Redux/store';
import usePreselections from 'Components/PickAndMix/Hooks/usePreselections';
import ProductSlimBanner from 'Components/Banner/ProductSlimBanner/ProductSlimBanner';
import CouponBannerOffer from 'Components/Product/ProductOffers/CouponBannerOffer';
import { IsChallenger } from 'Redux/ducks/split-test';
import { toggleBundleExitOfferModal } from 'Redux/ducks/modal';
import BundleExitOfferModal from 'Components/Bundle/BundleExitOfferModal';

import 'Components/PickAndMix/PickAndMixProductPage.scss';

const PickAndMixProductPage = ({
  pickAndMix, selectedProducts, userLibrary, steamLibrary, isPreview, wishlistSlugs,
}) => {
  const validTabs = ['recommended', 'all'];
  const tabParam = getUrlParameter('tab');
  const validTabParam = validTabs.includes(tabParam) ? tabParam : 'all';

  const [carouselGame, selectCarouselGame] = useState(1);
  const [currentTab, setCurrentTab] = useState(validTabParam);
  const [triggeredModal, setTriggeredModal] = useState(false);
  const { isBelowLg } = useViewport();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const sortedTiers = pickAndMix?.tiers?.sort((a, b) => a.quantity - b.quantity) || [];
  const minTier = sortedTiers[0] || null;
  const tier2 = sortedTiers[1] || null;
  const minTierQuantity = minTier?.quantity || 0;
  const tier2Quantity = tier2?.quantity || Infinity;

  const itemCount = selectedProducts?.length || 0;
  const hasMinTierItems = itemCount >= minTierQuantity;
  const belowTier2 = itemCount < tier2Quantity;
  const shouldShowModal = hasMinTierItems && belowTier2;

  useEffect(() => {
    const handleMouseMove = (e) => {
      // Find the navigation element and get its height
      const navElement = document.querySelector('.navbar');
      if (!navElement) return;

      const navRect = navElement.getBoundingClientRect();
      const isInNavArea = e.clientY <= navRect.bottom;

      // Trigger the exit offer modal when the user is in the navigation area and have hit the first tier
      if (isInNavArea && !triggeredModal && shouldShowModal && IsChallenger('BYOExitOfferModal')) {
        setTriggeredModal(true);
      }
    };

    // Trigger the exit offer modal when the user leaves the page and have hit the first tier
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !triggeredModal && shouldShowModal && IsChallenger('BYOExitOfferModal')) {
        setTriggeredModal(true);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [triggeredModal, shouldShowModal]);

  // Separate effect to handle modal triggering
  useEffect(() => {
    if (triggeredModal) {
      dispatch(toggleBundleExitOfferModal());
    }
  }, [triggeredModal, dispatch]);

  const {
    slug,
    name,
    description: shortDesc,
    aboutL: longDesc,
    cover_image: cover,
    tiers,
    products,
    type,
  } = pickAndMix;

  const sortedProducts =
    isPreview || pickAndMix.manual_sort ? products : sortPickAndMixProducts(
      products, userLibrary, steamLibrary, wishlistSlugs,
    );

  const updatedProducts = sortedProducts.map((game, index) => ({
    ...game,
    totalGameIndex: index + 1,
  }));

  const hits = updatedProducts.map(productToHit);

  const preselections = usePreselections(
    hits, tiers, selectedProducts, userLibrary, steamLibrary, slug,
  );

  const firstTier = _get(tiers, '[0]');

  const switchCarouselGame = (gameNumber) => {
    selectCarouselGame(gameNumber);
  };

  const handleTabChange = (tab) => {
    if (tab === 'all') {
      history.replace({ search: '' });
    }
    setCurrentTab(tab);
  };

  if (isEmpty(products)) return <PickAndMixNoProducts pickAndMix={pickAndMix} />;

  const PickAndMixLayoutComponent = isBelowLg ? PickAndMixMobileLayout : PickAndMixDesktopLayout;

  const tabOptions = [
    { name: formatMessage(messages.all), type: 'all' },
    { name: formatMessage(messages.recommendedForYou), type: 'recommended' },
  ];

  const hasPreselected = Array.isArray(preselections) && preselections.length > 0;
  const showRecommendedTab = hasPreselected && firstTier?.quantity > 1;
  const bundleTypeMessage = formatMessage(getBundleTypeMessage(type, products));

  const showSlimBanner = name === 'Build your own Bundle: Command: Modern Operations DLC Campaigns';

  const collections = _get(pickAndMix, 'collections', []);
  let showCouponBanner = collections.some(collection => collection.toLowerCase() === 'fantasyverse');
  const lunarSaleEnds = '2025-02-02T11:00:00.000Z';
  if (Date.now() > Date.parse(lunarSaleEnds)) {
    showCouponBanner = false;
  }

  return (
    <main className="PickAndMixProductPage">
      <Meta
        title={`${name} | Fanatical`}
        url={`pick-and-mix/${slug}`}
        description={shortDesc}
        image={`product/1280x720/${cover}`}
        pageType="ItemPage"
      />
      <ItemList noLinks title={`${bundleTypeMessage} in this Bundle`} hits={hits} />
      <BundleBreadcrumbs type="pick-and-mix" product={pickAndMix} />
      <BundleProductBar pickAndMix={pickAndMix} />
      <BundleExitOfferModal />

      {showSlimBanner &&
        <ProductSlimBanner />
      }

      <div className="PickAndMixProductPage__backgroundContrast">
        <div className="PickAndMixProductPage__backgroundContrastSlash" />
      </div>

      <PickAndMixLayoutComponent
        pickAndMix={pickAndMix}
        selectedProducts={selectedProducts}
        switchCarouselGame={switchCarouselGame}
        hits={hits}
        currentTab={currentTab}
        handleTabChange={handleTabChange}
        tabOptions={tabOptions}
        showRecommendedTab={showRecommendedTab}
        preselections={preselections}
      />

      {currentTab === 'all' &&
        <div className="container">
          {type === 'book-bundle' &&
            <EbookTrustBar slug={slug} />
          }


          {!isBelowLg &&
            <>
              <h1 className="PickAndMixProductPage__content__title">{name}</h1>
              <div className="PickAndMixProductPage__content__shortDesc">
                <DangerousHTML
                  html={shortDesc}
                />
              </div>
            </>
          }

          <BundleCarousel
            allGames={updatedProducts}
            totalGameCount={updatedProducts.length}
            product={{
              name,
              bundles: [],
              type: 'bundle',
              display_type: 'pick-and-mix',
            }}
            selectedSlide={carouselGame}
          />


          {longDesc && longDesc.length > 0 &&
            <section className="row mb-6">
              <div className="col-lg-7 about-section">
                <h2>
                  <FormattedMessage id="PickAndMixProductPage.aboutTitle" defaultMessage="About this Bundle" tagName="span" />
                </h2>

                <DangerousHTML
                  html={longDesc}
                />
              </div>
              <aside className="col-lg-5 about-section-aside">
                {showCouponBanner &&
                  <CouponBannerOffer />
                }
                <FreeProductContainer product={updatedProducts[0]} />
                <VoucherDelivery product={updatedProducts[0]} />
              </aside>
            </section>
          }
        </div>
      }
    </main>
  );
};

PickAndMixProductPage.propTypes = {
  pickAndMix: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    cover_image: PropTypes.string,
    type: PropTypes.string,
    tiers: PropTypes.arrayOf(PropTypes.object),
    valid_until: PropTypes.string,
    valid_from: PropTypes.string,
    slug: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.object),
    aboutL: PropTypes.string,
  }),
  selectedProducts: PropTypes.arrayOf(PropTypes.object),
  userLibrary: PropTypes.arrayOf(PropTypes.string),
  steamLibrary: PropTypes.arrayOf(PropTypes.string),
  isPreview: PropTypes.bool,
  wishlistSlugs: PropTypes.arrayOf(PropTypes.string),
};

PickAndMixProductPage.defaultProps = {
  pickAndMix: null,
  selectedProducts: [],
  userLibrary: [],
  steamLibrary: [],
  isPreview: false,
  wishlistSlugs: [],
};

const mapStateToProps = ({
  userLibrary,
  steamLibrary,
  wishlist: { allSlugs: wishlistSlugs },

}) => ({
  userLibrary,
  steamLibrary,
  wishlistSlugs,
});

export default connect(mapStateToProps)(PickAndMixProductPage);
